/**
 * The slim icon rail. Icons only, 56px wide, tooltip on hover and a real
 * accessible name on every control — the rail is the one piece of chrome that
 * is always on screen, so it stays out of the way.
 *
 * On mobile it is not rendered at all; `BottomBar` takes over.
 */
import { Link, useLocation } from 'react-router-dom'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAlerts } from '@/lib/queries'
import { toNumber } from '@/lib/format'
import { cn } from '@/lib/utils'
import { NAV_ITEMS, type NavItem } from './nav'

export function IconRail() {
  const location = useLocation()
  // Cheap: one row, and it drives the only badge in the chrome.
  const { data: alerts } = useAlerts({ unacknowledged: true, take: 1 })
  const unacknowledged = toNumber(alerts?.totalCount) ?? 0

  return (
    <nav
      aria-label="Sections"
      // `overflow-y-auto` because twelve destinations do not fit a 600px-tall
      // laptop viewport, and a rail that silently clips Settings is worse than
      // one that scrolls.
      className="border-hairline bg-panel fixed inset-y-0 left-0 z-30 hidden w-14 flex-col items-center gap-1 overflow-y-auto border-r py-3 md:flex"
    >
      <span
        className="font-display text-cyan mb-2 grid size-8 place-items-center text-sm font-bold"
        aria-hidden
      >
        PF
      </span>

      {NAV_ITEMS.map((item) => {
        const Icon = item.icon
        const active = isActive(item, location.pathname)
        const badge = item.to === '/alerts' && unacknowledged > 0
        const name = badge ? `${item.label} — ${unacknowledged} unacknowledged` : item.label

        return (
          <Tooltip key={item.to}>
            {/*
              `asChild` clones this element through Radix's Slot, which merges
              className as a string — so NavLink's function form of className
              would be stringified into the markup. Active state is computed
              from the location instead.
            */}
            <TooltipTrigger asChild>
              <Link
                to={item.to}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'relative grid size-10 place-items-center rounded-md',
                  'text-dim hover:bg-panel-2 hover:text-foreground',
                  'focus-visible:outline-ring focus-visible:outline-2 focus-visible:outline-offset-2',
                  active && 'bg-panel-2 text-cyan',
                )}
              >
                {/* The active marker is a bar as well as a color. */}
                {active && (
                  <span
                    className="bg-cyan absolute top-1/2 -left-2 h-5 w-0.5 -translate-y-1/2 rounded-r"
                    aria-hidden
                  />
                )}
                <Icon className="size-[1.125rem]" aria-hidden />
                {badge && (
                  <span
                    className="bg-rose ring-panel absolute top-1.5 right-1.5 size-2 rounded-full ring-2"
                    aria-hidden
                  />
                )}
                <span className="sr-only">{name}</span>
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">{name}</TooltipContent>
          </Tooltip>
        )
      })}
    </nav>
  )
}

function isActive(item: NavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}
